"use client";

import {
  PRESENTATION_PROJECT_ID,
  isPathAllowedInPresentationMode,
} from "@/app/_utils/constants/presentationMode.ts";
import { PresentationModeContext } from "@/app/_utils/context/PresentationModeContext.tsx";
import { UserContext } from "@/app/_utils/context/UserDataContext.tsx";
import { usePathname, useRouter } from "next/navigation";
import { useContext, useLayoutEffect } from "react";

/**
 * Client-side redirect when the current path is not allowed while presentation mode is on.
 * Uses `next/navigation` directly (no progress bar needed for this redirect).
 * Uses `presentationModeEnabled` from context (Cognito-backed flag on `user`).
 *
 * **Server vs client:** Do not rely on this guard for security. **`proxy.ts` (middleware)**
 * enforces presentation routes from the **verified ID token** on every matching request; that
 * is the real boundary (works with JS off, blocks direct URL access). This component aligns the
 * client on in-app navigations after the token on `user` updates.
 *
 * @returns Nothing rendered.
 */
export default function PresentationModeRouteGuard(): null {
  const { user } = useContext(UserContext);
  const { presentationModeEnabled } = useContext(PresentationModeContext);
  const pathname = usePathname();
  const router = useRouter();

  useLayoutEffect(() => {
    if (user === null || !presentationModeEnabled) {
      return;
    }

    if (isPathAllowedInPresentationMode(pathname)) {
      return;
    }

    router.replace(`/${PRESENTATION_PROJECT_ID}`);
  }, [user, presentationModeEnabled, pathname, router]);

  return null;
}
